import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { AsyncState } from '@peca/backoffice/models/async-state.model';
import { BreadcrumbItem, KeyTab, Tab } from '@peca/design-system';
import { BehaviorSubject } from 'rxjs';
import { PurchaseOrder } from '../../../models/purchase-order/purchase-order.model';
import { PurchaseOrderService } from '../../../services/purchase.service';
import { ActivatedRoute } from '@angular/router';
import { ToastService } from '@peca/commons';
import { AnalyticsService } from '@peca/commons';
import { getPurchaseOrderInvoiceLabel } from '../../../utils/purchase-order-invoice-status.utils';
import { PurchaseOrderInvoiceLabel } from '../../../models/purchase-order/purchase-order-invoice-status.model';
import { PurchaseOrderModalAssociationNfComponent } from '../purchase-order-modal-association-nf/purchase-order-modal-association-nf.component';

type State = AsyncState<PurchaseOrder>;

@Component({
  selector: 'peca-purchase-order-details',
  templateUrl: './purchase-order-details.component.html',
  styleUrls: ['./purchase-order-details.component.scss'],
  standalone: false,
})
export class PurchaseOrderDetailsComponent implements OnInit {
  private _useRouting = true;

  @ViewChild('modalAssociationNf')
  modalAssociationNf!: PurchaseOrderModalAssociationNfComponent;

  @Input()
  orderId = '';

  @Input()
  set useRouting(value: boolean) {
    this._useRouting = value;
    this.tabs = this.initTabs();
  }

  get useRouting() {
    return this._useRouting;
  }

  order$: BehaviorSubject<State>;
  breadcrumb: BreadcrumbItem[];
  tabs: KeyTab;
  service: PurchaseOrderService;
  route: ActivatedRoute;
  toast: ToastService;
  analytics: AnalyticsService;

  constructor() {
    this.breadcrumb = [
      { label: 'Compras', path: '/compras' },
      { label: 'Pedidos de Compra', path: '/compras/pedidos-de-compra' },
    ];
    this.order$ = new BehaviorSubject<State>({ loading: true });
    this.tabs = this.initTabs();
    this.service = inject(PurchaseOrderService);
    this.route = inject(ActivatedRoute);
    this.toast = inject(ToastService);
    this.analytics = inject(AnalyticsService);
  }

  get selectedTab() {
    const tab = Object.values<Tab>(this.tabs).find(({ active }) => active);
    if (!tab) return this.tabs[0];
    return tab;
  }

  get id() {
    if (!this.orderId) return this.route.snapshot.params['id'] as string;
    return this.orderId as string;
  }

  get showAddNfeButton() {
    const order = this.order$.value.data;
    if (!order) return false;

    return getPurchaseOrderInvoiceLabel(order) === PurchaseOrderInvoiceLabel.NOT_ASSOCIATED;
  }

  initTabs() {
    const tabs = {
      products: { label: 'Produtos', active: true, roles: [] },
      logs: { label: 'Logs', roles: [] },
      integrateLogs: { label: 'Integração', roles: [] },
    } as KeyTab;

    if (this.useRouting) {
      tabs['products'].path = 'produtos';
      tabs['logs'].path = 'logs';
      tabs['integrateLogs'].path = 'integrate-logs';
    }

    return tabs;
  }

  ngOnInit() {
    this.fetchOrder(this.id);
  }

  fetchOrder(id: string) {
    this.order$.next({ loading: true });
    this.service.fetchOrderDetails(id).subscribe({
      next: (order) => {
        this.order$.next({ data: order });
        this.breadcrumb.push({ label: order.order!.commerceReference });
      },
      error: () => this.order$.next({ failure: true }),
    });
  }

  onTabSelected(tab: Tab) {
    this.selectedTab.active = false;
    tab.active = true;
  }
  onClickAssociation() {
    this.modalAssociationNf.open();
  }

  handleUpload(file: File) {
    if (!file) return;

    const formData = new FormData();
    const order = this.order$.value.data;

    if (!order) return;

    formData.append('file', file);
    this.service.uploadNfe(order.order!.id, order.id, formData).subscribe({
      next: () => {
        this.ngOnInit();
        this.toast.success('NFe anexado com sucesso.');
      },
      error: () => {
        this.toast.failure('Falha ao anexar NFe.');
      },
    });
  }

  onRetry() {
    this.fetchOrder(this.id);
  }

  associate(invoiceId: string) {
    if (!invoiceId) return;

    this.service.associate(this.id, invoiceId).subscribe({
      next: () => {
        this.fetchOrder(this.id);
        this.toast.success('Associação concluída com sucesso');
      },
      error: () => {
        this.toast.failure('Falha ao associar NF-e.');
      },
    });
  }

  onClickIntegrate() {
    const order = this.order$.value.data;
    if (!order) return;

    if (order.status === 'WAITING_CONFIRMATION') {
      if (order.metadata?.failureAt == null || (order.metadata?.integrateAt != null && order.metadata?.failureAt <= order.metadata?.integrateAt)) {
        this.toast.warning('Integração em andamento. Não é possível integrar novamente.');
        return;
      }
    }

    this.service.integrate(order.id).subscribe({
      next: () => {
        this.fetchOrder(this.id);
        this.toast.success('Integração concluída com sucesso');
      },
      error: () => {
        this.toast.failure('Falha ao integrar pedido de compra.');
      },
    });
  }
}
